import { useState } from "react";
import { useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import Fade from "@mui/material/Fade";
import { useAppDispatch } from "hooks/redux";
import { snackbarRequest } from "shared/molecules/snackbar/snackbar-slice";
import authClientService from "utils/graphql/authClient";
import { CREATE_GOOGLE_WORKSPACE_USER } from "utils/graphql/services/live-classes/mutations";

type WorkspaceConfirmState = {
  open: boolean;
  profileIds: number[];
  admissionNumbers: string[];
};

export function useGoogleWorkspaceMapping(scheduleLoading: boolean) {
  const dispatch = useAppDispatch();
  const [workspaceConfirm, setWorkspaceConfirm] = useState<WorkspaceConfirmState>({
    open: false,
    profileIds: [],
    admissionNumbers: [],
  });
  const [createGoogleWorkspaceUser, { loading: creatingWorkspaceUsers }] =
    useMutation(CREATE_GOOGLE_WORKSPACE_USER, {
      client: authClientService,
    });

  const handleGoogleWorkspaceMappingError = (error: any) => {
    const graphQLError = error?.graphQLErrors?.[0];
    if (
      graphQLError?.extensions?.error_code !==
      "GOOGLE_WORKSPACE_MAPPING_NOT_FOUND"
    ) {
      return false;
    }

    const data = graphQLError?.extensions?.data;
    const rawProfileIds = data?.ProfileIds || data?.profileIds || [];
    const rawAdmissionNumbers = data?.admissionNumbers || [];
    const profileIds = Array.isArray(rawProfileIds)
      ? rawProfileIds.map(Number).filter((id: number) => Number.isFinite(id))
      : [];
    const admissionNumbers = Array.isArray(rawAdmissionNumbers)
      ? rawAdmissionNumbers.map(String)
      : [];

    setWorkspaceConfirm({ open: true, profileIds, admissionNumbers });
    return true;
  };

  const handleCreateWorkspaceUsers = async () => {
    try {
      const response = await createGoogleWorkspaceUser({
        variables: {
          input: {
            entityType: "STUDENT",
            entityIds: workspaceConfirm.profileIds,
          },
        },
      });
      const createdUsers = response?.data?.createGoogleWorkspaceUser;
      if (!Array.isArray(createdUsers) || createdUsers.length === 0) {
        dispatch(
          snackbarRequest({
            message: "Google Workspace users were not created",
            type: "error",
          }),
        );
        return;
      }

      setWorkspaceConfirm({ open: false, profileIds: [], admissionNumbers: [] });
      dispatch(
        snackbarRequest({
          message: `Created ${createdUsers.length} Google Workspace user(s) successfully`,
        }),
      );
    } catch (error: any) {
      dispatch(
        snackbarRequest({
          message:
            error?.graphQLErrors?.[0]?.message ||
            error?.message ||
            "Failed to create Google Workspace users",
          type: "error",
        }),
      );
    }
  };

  const dialog = (
    <Dialog
      open={workspaceConfirm.open}
      onClose={() =>
        setWorkspaceConfirm({ open: false, profileIds: [], admissionNumbers: [] })
      }
      TransitionComponent={Fade}
      transitionDuration={{ enter: 50, exit: 50 }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 700 }}
      >
        Google Workspace Account Missing
        <IconButton
          aria-label="Close dialog"
          onClick={() =>
            setWorkspaceConfirm({ open: false, profileIds: [], admissionNumbers: [] })
          }
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" gutterBottom>
          Some selected students do not have an active Google Workspace account.
          Without an active account, these students will not be able to attend
          their live classes.
          <br />
          <Typography
            component="span"
            sx={{ fontWeight: 500, display: "block", mt: 1 }}
          >
            Do you want to create Google Workspace accounts for these students?
          </Typography>
        </Typography>
        {workspaceConfirm.admissionNumbers.length > 0 && (
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              bgcolor: "#fff3e0",
              borderRadius: 1,
              border: "1px solid #ffb74d",
              maxHeight: 150,
              overflowY: "auto",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
            >
              Affected Admission Numbers:
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
              {workspaceConfirm.admissionNumbers.join(", ")}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <LoadingButton
          variant="contained"
          loading={scheduleLoading || creatingWorkspaceUsers}
          onClick={handleCreateWorkspaceUsers}
        >
          Create Google workspace
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );

  return { handleGoogleWorkspaceMappingError, dialog };
}
